import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLoaderData, data } from 'react-router'
import type { LinksFunction, LoaderFunctionArgs } from 'react-router'
import { getSecurityHeaders } from '~/lib/security.server'
import { getSessionUser } from '~/lib/auth.server'
import { LoadingBar } from '~/components/loading-bar'
import { ToastProvider } from '~/components/toast'
import { WebsiteSchema, OrganizationSchema } from '~/components/structured-data'
import { GlobalErrorBoundary } from '~/components/error-boundary'
import { MobileBottomNav } from '~/components/mobile-nav'
import { SentryScript, PostHogScript } from '~/components/analytics'
import { DeferredOverlays } from '~/components/deferred-overlays'
import './styles/app.css'

export const links: LinksFunction = () => [
  { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
  // Apple Home Screen + Safari pinned tab + Apple Business Connect surface
  // recognise these. SVG is honoured by Safari 18+ / iOS 18+; older iOS
  // falls back to favicon.svg via the precomposed alias.
  { rel: 'apple-touch-icon', href: '/favicon.svg' },
  { rel: 'apple-touch-icon-precomposed', href: '/favicon.svg' },
  { rel: 'mask-icon', href: '/favicon.svg', color: '#2563eb' },
  { rel: 'manifest', href: '/manifest.webmanifest' },
  { rel: 'preload', href: '/fonts/inter-latin-400.woff2?v=2', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
  { rel: 'preload', href: '/fonts/inter-latin-600.woff2?v=2', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
  { rel: 'preload', href: '/fonts/inter-latin-700.woff2?v=2', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
]

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="fr" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <meta property="og:site_name" content="AfficheServices" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:type" content="website" />
        {/* og:image is set per-page (seoMeta / page meta) so the social card
            matches the page. Removed the global default here to avoid a second,
            generic og:image tag winning over the page-specific one. */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="theme-color" content="#2563eb" />
        <meta name="google-site-verification" content="FRzGTryn1JRmJ33TAsKsmPjg5-9yFQFaeBCkkgTzQNs" />
        {/* Hreflang removed (audit #21) — French-only site means a single
            self-pointing hreflang is just noise that Google ignores. Add
            back as `fr-FR` and `fr-BE` etc. if/when we ship locale variants. */}
        {/* No global geo meta: a static "Paris, France" is wrong on every
            non-Paris page and duplicates the per-page tags that geoMeta()
            emits on city routes (DataForSEO flagged geo.region/placename as
            duplicate_meta_tags). City pages set their own; national pages
            need none. */}
        {/* Performance hints */}
        <link rel="dns-prefetch" href="https://api-adresse.data.gouv.fr" />
        {/* dns-prefetch only (cheap DNS warm) — the full preconnect opened an
            unused TLS connection to Unsplash on the ~95% of pages that have no
            Unsplash image. Image-heavy pages (home, blog) still benefit from
            the warm DNS. */}
        <link rel="dns-prefetch" href="https://images.unsplash.com" />
        {/* RSS — freshness/discovery channel for the blog */}
        <link rel="alternate" type="application/rss+xml" title="AfficheServices — Blog" href="https://www.afficheservices.fr/blog/rss.xml" />
        <Meta />
        <Links />
      </head>
      <body className="min-h-screen bg-white font-sans text-gray-900 antialiased">
        <a href="#main-content" className="skip-link">Aller au contenu principal</a>
        <noscript>
          <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'system-ui', background: '#fef3c7', color: '#92400e' }}>
            JavaScript est requis pour utiliser AfficheServices. Veuillez activer JavaScript dans votre navigateur.
          </div>
        </noscript>
        {children}
        <ScrollRestoration />
        <Scripts />
        <SentryScript />
        <PostHogScript />
        {/* Service Worker registration — hardcoded, safe */}
        <script dangerouslySetInnerHTML={{ __html: `if('serviceWorker' in navigator){navigator.serviceWorker.register('/sw.js').catch(function(){})}` }} />
      </body>
    </html>
  )
}

export async function loader({ request }: LoaderFunctionArgs) {
  // Validate required env on the first request (audit #8). React Router 7
  // strips server-only modules from the client bundle only when they're
  // referenced inside loader/action/headers/middleware, so the import
  // can't sit at module top-level.
  await import('~/lib/env.server')
  const { getVapidPublicKey } = await import('~/lib/push.server')
  const user = await getSessionUser(request).catch(() => null)
  // NOTE: we deliberately do NOT set any Set-Cookie here. The old csrf_token
  // cookie was dead code (validateCsrf was never called, no form submitted it)
  // and its mere presence made every response uncacheable at the Cloudflare
  // edge (CF bypasses cache on Set-Cookie). Removing it lets anonymous SEO
  // pages be edge-cached. State-changing actions stay protected by rate limits
  // + SameSite=Lax session cookie; a Cloudflare cache rule bypasses cache when
  // the `session_id` cookie is present so logged-in HTML is never shared.
  const resHeaders = new Headers(getSecurityHeaders())

  // Return data object + headers via `data()` helper so useLoaderData
  // properly infers the shape. Previously used Response.json() which
  // collapsed the type to `never` and broke typecheck in root & components.
  return data(
    {
      user: user ? { firstName: user.firstName, role: user.role } : null,
      crispId: process.env.CRISP_WEBSITE_ID ?? '',
      partykitHost: process.env.PARTYKIT_HOST ?? '',
      vapidPublicKey: getVapidPublicKey() ?? '',
    },
    { headers: resHeaders },
  )
}

export { GlobalErrorBoundary as ErrorBoundary }

export default function App() {
  const data = useLoaderData<typeof loader>()

  return (
    <ToastProvider>
      <WebsiteSchema />
      <OrganizationSchema />
      <LoadingBar />
      <div id="main-content" className="pb-mobile-nav animate-page-in">
        <Outlet />
      </div>
      <MobileBottomNav />
      {/* All non-critical overlays (chat, cookies, push, back-to-top,
          newsletter, PWA, sticky CTA, keyboard shortcuts) are deferred until
          first interaction or browser idle — see audit #8. */}
      <DeferredOverlays crispId={data?.crispId} />
      {data?.partykitHost && (
        <script dangerouslySetInnerHTML={{ __html: `window.__PARTYKIT_HOST="${data.partykitHost}"` }} />
      )}
    </ToastProvider>
  )
}
